import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AgentAPI from '../../api/agents';
import OfficialSellersAPI from '../../api/aceleres/officialSellers';

export const state = {
  records: [],
  officialSellerRecords: [],
  officialSellerManagementRecords: [],
  uiFlags: {
    isFetching: false,
    isFetchingOfficialSellers: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAgents($state) {
    return $state.records;
  },
  getOfficialSellers($state) {
    return $state.officialSellerRecords;
  },
  getOfficialSellerManagement($state) {
    return $state.officialSellerManagementRecords;
  },
  getVerifiedAgents($state) {
    return $state.records.filter(record => record.confirmed);
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getAgentById: $state => id => {
    return $state.records.find(record => record.id === Number(id)) || {};
  },
  getAgentStatus($state) {
    let status = {
      online: $state.records.filter(
        agent => agent.availability_status === 'online'
      ).length,
      busy: $state.records.filter(agent => agent.availability_status === 'busy')
        .length,
      offline: $state.records.filter(
        agent => agent.availability_status === 'offline'
      ).length,
    };
    return status;
  },
};

const refreshOfficialSellerState = async dispatch => {
  await Promise.all([
    dispatch('getOfficialSellers'),
    dispatch('getOfficialSellerManagement'),
    dispatch('get'),
  ]);
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_AGENT_FETCHING_STATUS, true);
    try {
      const response = await AgentAPI.get();
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
      commit(types.default.SET_AGENTS, response.data);
    } catch (error) {
      commit(types.default.SET_AGENT_FETCHING_STATUS, false);
    }
  },
  getOfficialSellers: async ({ commit }) => {
    commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', true);
    try {
      const response = await OfficialSellersAPI.get();
      commit('SET_OFFICIAL_SELLERS', response.data);
      commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', false);
    } catch (error) {
      commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', false);
    }
  },
  getOfficialSellerManagement: async ({ commit }) => {
    commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', true);
    try {
      const response = await OfficialSellersAPI.get({ includeInactive: true });
      commit('SET_OFFICIAL_SELLER_MANAGEMENT', response.data);
      commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', false);
    } catch (error) {
      commit('SET_OFFICIAL_SELLERS_FETCHING_STATUS', false);
    }
  },
  syncOfficialSeller: async ({ dispatch }, chatwootUserId) => {
    try {
      await OfficialSellersAPI.sync(chatwootUserId);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  createOfficialSeller: async ({ dispatch }, seller) => {
    try {
      await OfficialSellersAPI.create(seller);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  updateOfficialSeller: async ({ dispatch }, { chatwootUserId, seller }) => {
    try {
      await OfficialSellersAPI.update(chatwootUserId, seller);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  deactivateOfficialSeller: async ({ dispatch }, chatwootUserId) => {
    try {
      await OfficialSellersAPI.deactivate(chatwootUserId);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  reactivateOfficialSeller: async ({ dispatch }, sellerIds) => {
    try {
      await OfficialSellersAPI.reactivate(sellerIds);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  deleteLocalOfficialSeller: async ({ dispatch }, aceleresUserId) => {
    try {
      await OfficialSellersAPI.deleteLocal(aceleresUserId);
    } finally {
      await refreshOfficialSellerState(dispatch);
    }
  },
  create: async ({ commit }, agentInfo) => {
    commit(types.default.SET_AGENT_CREATING_STATUS, true);
    try {
      const response = await AgentAPI.create(agentInfo);
      commit(types.default.ADD_AGENT, response.data);
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      return response.data;
    } catch (error) {
      commit(types.default.SET_AGENT_CREATING_STATUS, false);
      throw error;
    }
  },
  update: async ({ commit }, { id, ...agentParams }) => {
    commit(types.default.SET_AGENT_UPDATING_STATUS, true);
    try {
      const response = await AgentAPI.update(id, agentParams);
      commit(types.default.EDIT_AGENT, response.data);
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_UPDATING_STATUS, false);
      throw new Error(error);
    }
  },
  updateSingleAgentPresence: ({ commit }, { id, availabilityStatus }) => {
    commit(types.default.UPDATE_SINGLE_AGENT_PRESENCE, {
      id,
      availabilityStatus,
    });
  },
  updatePresence: async ({ commit }, data) => {
    commit(types.default.UPDATE_AGENTS_PRESENCE, data);
  },
  delete: async ({ commit }, agentId) => {
    commit(types.default.SET_AGENT_DELETING_STATUS, true);
    try {
      await AgentAPI.delete(agentId);
      commit(types.default.DELETE_AGENT, agentId);
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
    } catch (error) {
      commit(types.default.SET_AGENT_DELETING_STATUS, false);
      throw new Error(error);
    }
  },
};

export const mutations = {
  [types.default.SET_AGENT_FETCHING_STATUS]($state, status) {
    $state.uiFlags.isFetching = status;
  },
  SET_OFFICIAL_SELLERS_FETCHING_STATUS($state, status) {
    $state.uiFlags.isFetchingOfficialSellers = status;
  },
  SET_OFFICIAL_SELLERS($state, sellers) {
    $state.officialSellerRecords = sellers;
  },
  SET_OFFICIAL_SELLER_MANAGEMENT($state, sellers) {
    $state.officialSellerManagementRecords = sellers;
  },
  [types.default.SET_AGENT_CREATING_STATUS]($state, status) {
    $state.uiFlags.isCreating = status;
  },
  [types.default.SET_AGENT_UPDATING_STATUS]($state, status) {
    $state.uiFlags.isUpdating = status;
  },
  [types.default.SET_AGENT_DELETING_STATUS]($state, status) {
    $state.uiFlags.isDeleting = status;
  },

  [types.default.SET_AGENTS]: MutationHelpers.set,
  [types.default.ADD_AGENT]: MutationHelpers.create,
  [types.default.EDIT_AGENT]: MutationHelpers.update,
  [types.default.DELETE_AGENT]: MutationHelpers.destroy,
  [types.default.UPDATE_AGENTS_PRESENCE]: MutationHelpers.updatePresence,
  [types.default.UPDATE_SINGLE_AGENT_PRESENCE]: (
    $state,
    { id, availabilityStatus }
  ) =>
    MutationHelpers.updateSingleRecordPresence($state.records, {
      id,
      availabilityStatus,
    }),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
